// R115 maint — the maintenance layer, stats and chip move.
//   layer 'wo_unscheduled' (server provider of the same id): the 📥 bucket +
//     per-home counts; draws the 🔧 row badge (open WOs, tone by urgency /
//     overdue) that opens that home's bucket.
//   stats wo_open · wo_unscheduled · wo_booked_in_view · wo_overdue · wos_due —
//     KPI cards that double as the ONE active filter (rows / chips they match).
//   chip kind 'wo' + move: core registers the 'wo' chip kind (its lite popover);
//     maint ATTACHES the move (desktop drag onto a day = book / move, the same
//     guarded path as Book…) without replacing core's open — re-checked
//     whenever the page asks this module for anything, so registration order
//     between core and features never matters.
import { registerLayer, registerStat, registerChipKind, getChipKind, type CalendarCtx, type ChipKind, type ChipSpec, type EntityRef } from '../registry';
import type { Property } from '../types';
import { api, WO_API, LAYER_ID, OFFICE, woLayer } from './shared';
import { guardGuestNight } from './book';

// ── chip move ───────────────────────────────────────────────────────────────
const woMove: NonNullable<ChipKind['move']> = {
  canDrop: (ref: EntityRef, date: string, ctx: CalendarCtx) => ref.kind === 'wo' && /^\d{4}-\d{2}-\d{2}$/.test(String(date)) && date >= ctx.asOf,
  onDrop: async (ref: EntityRef, date: string, ctx: CalendarCtx) => {
    const ev = (ctx.data.events.work_orders || []).find(w => String(w.id) === String(ref.id));
    const un = (woLayer(ctx)?.unscheduled || []).find(w => w.id === String(ref.id));
    const listing = String(ref.listing_id || (ev && ev.listing_id) || (un && un.listing_id) || '');
    if (!listing) { ctx.toast("Couldn't tell which home that work order belongs to."); return; }
    const prev = ev && !ev.due_only ? ev.date : null;
    if (prev && prev === date) return;   // dropped where it already is
    // the guest-permission state isn't on the chip — read the record (one GET) so the
    // occupied-night ask only appears when permission is really missing
    let guestOk = !!(un && un.guest_ok) || !!(ev && ev.guest_ok);
    if (!un && !(ev && typeof ev.guest_ok === 'boolean')) { const r = await api('GET', `${WO_API}/${encodeURIComponent(String(ref.id))}`); guestOk = !!(r.ok && r.json && r.json.work_order && r.json.work_order.guest_permission && r.json.work_order.guest_permission.granted); }   // an older server's event has no flag
    guardGuestNight(ctx, { id: String(ref.id), listing_id: listing, title: (ev && ev.title) || (un && un.title) || 'Work order', prev, guest_ok: guestOk }, date);
  },
};
/** Attach the move to whatever 'wo' chip kind is registered (core's), or register a full one if none is. */
export function ensureWoMove(): void {
  const cur = getChipKind('wo');
  if (cur && cur.move === woMove) return;
  if (cur) { registerChipKind({ ...cur, move: woMove }); return; }
  registerChipKind({
    kind: 'wo', legend: 'Work order',
    open: (ref, _anchor, ctx) => ctx.openInspector({ kind: 'wo', id: String(ref.id), listing_id: ref.listing_id, date: ref.date }),
    move: woMove,
  });
}
ensureWoMove();

// ── layer ───────────────────────────────────────────────────────────────────
const counts = (ctx: CalendarCtx, lid: string) => { const l = woLayer(ctx); return l ? l.by_listing[lid] || null : null; };
registerLayer({
  id: LAYER_ID, label: 'Work orders to schedule', swatch: 'var(--info)', roles: OFFICE, scope: 'row', server: LAYER_ID,
  badges: (p: Property, ctx: CalendarCtx) => {
    ensureWoMove();
    const c = counts(ctx, p.listing_id);
    if (!c || !c.open) return [];
    const tone = c.urgent ? 'bad' : c.overdue ? 'warn' : 'muted';
    const bits = [`${c.open} open work order${c.open === 1 ? '' : 's'}`, c.unscheduled ? `${c.unscheduled} with no day booked` : '', c.overdue ? `${c.overdue} overdue` : '', c.urgent ? `${c.urgent} urgent / emergency` : ''].filter(Boolean);
    return [{ key: 'wo_open', glyph: '🔧', text: String(c.open), title: `${bits.join(' · ')} — open the list`, tone, entity: { kind: 'wo_bucket', id: p.listing_id, listing_id: p.listing_id } }];
  },
  count: (ctx) => { ensureWoMove(); const l = woLayer(ctx); return l ? l.counts.unscheduled : null; },
});

// ── stats (KPI cards = the one active filter) ───────────────────────────────
const isWoChip = (c: ChipSpec) => !!c && !!c.entity && c.entity.kind === 'wo';
const bookedEvents = (ctx: CalendarCtx) => (ctx.data.events.work_orders || []).filter(w => !w.due_only && w.status !== 'done');
const evById = (ctx: CalendarCtx, id: string) => (ctx.data.events.work_orders || []).find(w => String(w.id) === String(id)) || null;
registerStat({
  key: 'wo_open', label: 'Open work orders', icon: '🔧', layer: LAYER_ID,
  compute: (ctx) => { ensureWoMove(); const l = woLayer(ctx); if (!l) return null; return { value: l.counts.open, sub: `${l.counts.urgent} urgent · ${l.counts.unassigned} unassigned`, tone: l.counts.urgent ? 'warn' : undefined }; },
  match: { rows: (p, ctx) => !!(counts(ctx, p.listing_id) || { open: 0 }).open, chips: (c) => isWoChip(c) },
});
registerStat({
  key: 'wo_unscheduled', label: 'No day booked', icon: '📥', layer: LAYER_ID,
  compute: (ctx) => { const l = woLayer(ctx); if (!l) return null; return { value: l.counts.unscheduled, sub: 'in the 📥 bucket — book or drag', tone: l.counts.unscheduled ? 'warn' : 'good' }; },
  match: { rows: (p, ctx) => !!(counts(ctx, p.listing_id) || { unscheduled: 0 }).unscheduled, chips: (c, ctx) => isWoChip(c) && !!(evById(ctx, c.entity.id) || { due_only: false }).due_only },
});
registerStat({
  key: 'wo_booked_in_view', label: 'Booked in view', icon: '📆',
  compute: (ctx) => { const b = bookedEvents(ctx); const late = b.filter(w => (w.end || w.date) < ctx.asOf).length; return { value: b.length, sub: late ? `${late} day${late === 1 ? '' : 's'} passed — rebook or close` : `${ctx.fmt.md(ctx.from)} – ${ctx.fmt.md(ctx.to)}`, tone: late ? 'warn' : undefined }; },
  match: { rows: (p, ctx) => bookedEvents(ctx).some(w => w.listing_id === p.listing_id), chips: (c, ctx) => isWoChip(c) && !(evById(ctx, c.entity.id) || { due_only: true }).due_only },
});
registerStat({
  key: 'wo_overdue', label: 'Overdue', icon: '⏰', layer: LAYER_ID,
  compute: (ctx) => { const l = woLayer(ctx); if (!l) return null; return { value: l.counts.overdue, sub: 'past their due day', tone: l.counts.overdue ? 'bad' : 'good' }; },
  match: { rows: (p, ctx) => !!(counts(ctx, p.listing_id) || { overdue: 0 }).overdue, chips: (c, ctx) => { const e = evById(ctx, c.entity.id); return isWoChip(c) && !!(e && e.due_date && e.due_date < ctx.asOf); } },
});
registerStat({
  key: 'wos_due', label: 'WOs due ≤ 7 days', icon: '🔧', layer: LAYER_ID,
  compute: (ctx) => { const l = woLayer(ctx); if (!l) return null; return { value: l.counts.due_soon, sub: `${l.counts.overdue} already overdue`, tone: l.counts.overdue ? 'bad' : l.counts.due_soon ? 'warn' : 'good' }; },
  match: { rows: (p, ctx) => !!(counts(ctx, p.listing_id) || { due_soon: 0 }).due_soon, chips: (c, ctx) => { const e = evById(ctx, c.entity.id); return isWoChip(c) && !!(e && e.due_date && e.due_date <= ctx.fmt.addDays(ctx.asOf, 7)); } },
});
